import {
    RiAccountCircleLine,
    RiAdminLine,
    RiArrowRightSLine,
    RiCheckDoubleLine,
    RiCodeLine,
    RiComputerLine,
    RiEditCircleLine,
    RiFacebookLine, RiHome6Line, RiLifebuoyLine,
    RiLightbulbFlashFill,
    RiLink, RiLinkedinFill,
    RiLogoutBoxLine,
    RiLoopRightLine,
    RiMacLine,
    RiMoonLine,
    RiPaintBrushLine,
    RiPriceTag3Line,
    RiScales3Line,
    RiShieldCheckLine,
    RiSunLine,
    RiTwitterLine,
    RiUser3Fill,
} from "@remixicon/react";
import classNames from "classnames";
import React, {
    ReactNode, useEffect, useMemo, useState,
} from "react";
import {
    Nav,
    NavDropdown,
    Navbar,
} from "react-bootstrap";
import { AiOutlineGift } from "react-icons/ai";
import { BiBookHeart } from "react-icons/bi";
import { BsGearFill, BsPlusSquare } from "react-icons/bs";
import { FaChartPie, FaInbox, FaUsers } from "react-icons/fa";
import { FiHelpCircle } from "react-icons/fi";
import {
    IoPerson, IoTicketSharp,
} from "react-icons/io5";
import {
    MdOutlineMarkChatUnread, MdOutlineTipsAndUpdates,
} from "react-icons/md";
import {
    RiArrowLeftDoubleLine,
    RiBookMarkedLine,
    RiDashboardLine, RiGiftLine,
    RiQuestionLine,
    RiSettings3Line,
    RiTeamLine,
} from "react-icons/ri";
import { TbLayoutSidebarLeftCollapse } from "react-icons/tb";
import {
    AgentAssignmentPreference,
    Permission, PortalEventType,
    PortalSettingsIcon,
    PortalSettingsLinkVisibility,
} from "../../__generated__/api";
import { ReactComponent as RocketIcon } from "../../assets/icons/rocket-new.svg";
import logoV3 from "../../assets/img/logo-primary.svg";
import logo from "../../assets/img/logo-white.svg";
import useCreateTicket from "../../contexts/CreateTicketContext";
import { useHelpContext } from "../../contexts/HelpContext";
import usePortal from "../../contexts/PortalContext";
import useEvents, { useConnection } from "../../contexts/PortalSubscriptionContext";
import useRouting, { NavLink, Paths, isRouteMatch } from "../../contexts/Routing";
import { Themes, useTheme } from "../../contexts/Theme";
import useCurrentSession from "../../contexts/session";
import { useTicketState } from "../../contexts/ticket-state";
import useUpdatesContext from "../../contexts/updates/UpdatesContext";
import Avatar from "../avatar/Avatar";
import CodeBlock from "../code-block/CodeBlock";
import Confirm from "../confirm/Confirm";
import Help from "../help/Help";
import Button from "../theme/button/Button";
import Dropdown from "../theme/dropdown/Dropdown";
import Icon from "../theme/icon/Icon";
import "./Navigation.scss";
import Toggle from "../theme/toggle/Toggle";

/** Navigation Bar */

export const PORTAL_SETTINGS_ICON_IMAGES = {
    [PortalSettingsIcon.Code]: <RiCodeLine />,
    [PortalSettingsIcon.Linkedin]: <RiLinkedinFill />,
    [PortalSettingsIcon.Facebook]: <RiFacebookLine />,
    [PortalSettingsIcon.Homepage]: <RiHome6Line />,
    [PortalSettingsIcon.Status]: <RiCheckDoubleLine />,
    [PortalSettingsIcon.Security]: <RiShieldCheckLine />,
    [PortalSettingsIcon.Legal]: <RiScales3Line />,
    [PortalSettingsIcon.Support]: <RiLifebuoyLine />,
    [PortalSettingsIcon.Twitter]: <RiTwitterLine />,
    [PortalSettingsIcon.Website]: <RiLink />,
};

const ICON_SIZE = "24px";

const KEY_NAVIGATION = "navigation";

const FEATURE_SHOW_PAGE_HELP = "frontend.show.page.help";

interface NavigationSettings {
    isExpanded: boolean;
}

const BLOCKED_ROUTES = [
    Paths.ONBOARDING,
    Paths.ONBOARDING_STEP,
];

export const FEATURE_DARKER = "frontend.design.darker";

const ASSIGNMENT_PREFERENCES = [
    {
        label: "Auto",
        value: AgentAssignmentPreference.AutoSynchronize,
        icon: (
            <RiLoopRightLine className="icon-default" />
        ),
    },
    {
        label: "Online",
        value: AgentAssignmentPreference.Available,
        icon: (
            <span className={classNames("nav-avatar-status-picker-online", "nav-avatar-status-picker")} />
        ),
    },
    {
        label: "Offline",
        value: AgentAssignmentPreference.Unavailable,
        icon: (
            <span className={classNames("nav-avatar-status-picker-offline", "nav-avatar-status-picker")} />
        ),
    },
];
const ASSIGNMENT_PREFERENCES_MAP = ASSIGNMENT_PREFERENCES.toIdMap("value");

const ENTITLEMENT_TICKETING = "urn:fq:plan-entitlement:ticketing";

export default function Navigation({ shouldDisplayOnSide }: {
    shouldDisplayOnSide?: boolean;
}): ReactNode {
    const {
        isLoggedIn, user, logout, current: currentSession, clearCache, assignmentPreference, saveAssignmentPreference,
        permissions,
    } = useCurrentSession();

    const { entitlementIds, navigation } = usePortal();

    const theme = useTheme();
    const ticketState = useTicketState();

    const helpContext = useHelpContext();
    const {
        featureFlags, id: portalId, invalidate: invalidatePortal, permissions: portalPermissions,
    } = usePortal();

    const [settings, setSettings] = useState<NavigationSettings>(() => {
        try {
            let current = localStorage.getItem(KEY_NAVIGATION);
            if (!current) {
                current = JSON.stringify({
                    isExpanded: false,
                });
                localStorage.setItem(KEY_NAVIGATION, current);
            }
            return JSON.parse(current) as NavigationSettings;
        } catch (err) {
            const state = {
                isExpanded: false,
            };
            localStorage.setItem(KEY_NAVIGATION, JSON.stringify(state));
            return state as NavigationSettings;
        }
    });
    useEffect(() => {
        if (settings) {
            localStorage.setItem(KEY_NAVIGATION, JSON.stringify(settings));
        }
    }, [settings]);

    const updates = useUpdatesContext();

    const { createHistoryPush } = useRouting();

    const isRouteAgent = isRouteMatch({ path: Paths.AGENT, exact: false, strict: false });
    const isRouteTickets = isRouteMatch({ path: Paths.TICKETS, exact: false, strict: false });

    const [hasMessage, setHasMessages] = useState(false);
    const createTicket = useCreateTicket();

    const [showDebug, setShowDebug] = useState(false);
    const connectionApi = useConnection();

    useEffect(() => {
        if (hasMessage && (isRouteAgent || isRouteTickets)) {
            setHasMessages(false);
        }
    }, [isRouteAgent, isRouteTickets, hasMessage]);

    useEvents(api => {
        api.on(PortalEventType.MessageNew, 0, () => {
            setHasMessages(true);
        });
        api.on(PortalEventType.ConversationNew, 0, () => {
            setHasMessages(true);
        });
    });

    const navbarClasses = [];
    if (shouldDisplayOnSide) {
        navbarClasses.push("flex-grow-1 d-none d-lg-block");
        if (settings.isExpanded) {
            navbarClasses.push("");
        } else {
            navbarClasses.push("w-100 justify-content-between d-flex mx-0");
        }
    }

    const navBackground = theme.isV3 ? "nav-background" : "bg-blue-dark";

    let unreadBadge = null;
    const unreadCount = ticketState?.dashboardView?.getData().sum(column => column.unreadCount) ?? 0;
    if (theme.isV3 && unreadCount > 0) {
        unreadBadge = (
            <>
                <div className="unread-badge-container">
                    <span className="unread-badge">
                        {unreadCount > 100 ? "99+" : unreadCount}
                    </span>
                </div>
                <span className="visually-hidden">New alerts</span>
            </>
        );
    } else if (!theme.isV3 && hasMessage) {
        unreadBadge = (
            <span
                className={"position-absolute top-0 start-75 translate-middle "
                + "bg-danger badge p-1 rounded-circle"}
            >
                <MdOutlineMarkChatUnread />
                <span className="visually-hidden">New alerts</span>
            </span>
        );
    }

    const ticketsLink = (permissions.has(Permission.ChatsRead) && entitlementIds.has(entitlementIds.keys.TICKETING) ? (
        <NavLink
            to={Paths.AGENT}
            className={`navbar-link position-relative ${!theme.isV3 ? "d-block" : ""} ${
                shouldDisplayOnSide ? `mb-3 w-100 text-center text-lg-start` : "mx-lg-2 mx-2 px-1 "}`}
        >
            {shouldDisplayOnSide
                && (theme.isV3
                    ? <RiPriceTag3Line className="me-0 d-inline ticket-icon" size={ICON_SIZE} />
                    : <FaInbox className="me-0 d-inline" size={ICON_SIZE} />)}
            <span className={shouldDisplayOnSide
                ? "d-inline ms-2 nav-label"
                : ""}
            >
                Tickets
            </span>
            {unreadBadge}
        </NavLink>
    ) : null);

    const answersLink = ((portalPermissions.has(Permission.ArticleReadPublic) || permissions.has(Permission.ArticleReadPublic)) ? (
        <NavLink
            to={Paths.ANSWERS_LIST}
            className={`navbar-link ${shouldDisplayOnSide ? `mb-3 w-100 text-center text-lg-start` : "mx-lg-0 mx-2 "}`}
        >
            {shouldDisplayOnSide
                && (theme.isV3
                    ? <RiBookMarkedLine className="me-0 d-inline" size={ICON_SIZE} />
                    : <BiBookHeart className="me-0 d-inline" size={ICON_SIZE} />)}
            <span className={shouldDisplayOnSide
                ? "d-inline ms-2 nav-label"
                : ""}
            >
                Answers
            </span>
        </NavLink>
    ) : null);

    const [, incrementState] = useState(0);
    useEffect(() => {
        if (showDebug) {
            const timer = setInterval(() => {
                incrementState(value => value + 1);
            }, 1000);
            return () => {
                clearInterval(timer);
            };
        }
        return undefined;
    }, [showDebug]);

    const isBlocked = useMemo(
        () => BLOCKED_ROUTES.some(path => window.location.pathname.startsWith(path)),
        [window.location.pathname],
    );

    if (isBlocked) {
        return null;
    }

    const { connectionStatus } = connectionApi;
    const assignmentStatus = ASSIGNMENT_PREFERENCES_MAP[assignmentPreference];
    return (
        <>
            {showDebug && (
                <Confirm
                    title="Debugging Info"
                    size="lg"
                    shouldShow
                    onHide={() => setShowDebug(false)}
                >

                    <small>
                        Please only use these actions if you know exactly what they are needed for.
                    </small>

                    <div className="d-flex gap-2 mb-2">
                        <Button
                            onClick={() => {
                                clearCache();
                                invalidatePortal();
                            }}
                            size="small"
                            variant="outline-gray"
                        >
                            Clear Cache
                        </Button>
                        <Button
                            onClick={() => {
                                connectionApi.close();
                            }}
                            size="small"
                            variant="outline-gray"
                        >
                            Reset Connection
                        </Button>
                    </div>

                    <div>
                        <p>Please send the following information to support</p>
                        <div>
                            <CodeBlock
                                code={
                                    Object.entries({
                                        "App Version": window.FOQAL_VERSION_NUMBER ?? "",
                                        "User Id": user?.id,
                                        "Session Id": currentSession?.id,
                                        "Portal Id": portalId,
                                        Group: user?.group,
                                        Role: user?.role,
                                        "User Groups": user?.userGroups?.toIdList("id"),
                                        Permissions: Array.from(permissions),
                                        "Feature Flags": Array.from(featureFlags),
                                        "Connection Status": !connectionStatus || connectionStatus?.isConnecting
                                            ? "Connecting"
                                            : "Connected",
                                        "Messages Since Connection": connectionStatus?.messageCount,
                                        "Last Message": connectionStatus?.lastMessage
                                            ? new Date(connectionStatus?.lastMessage).toLocaleString()
                                            : undefined,
                                        "Last Connected": connectionStatus?.lastConnected
                                            ? new Date(connectionStatus?.lastConnected).toLocaleString()
                                            : undefined,
                                    })
                                        .filter(([, value]) => value != null)
                                        .map(([key, value]) => {
                                            const formatted = Array.isArray(value) ? `\n\t${value.join(",\n\t")}` : value;
                                            return `${key}: ${formatted}`;
                                        })
                                        .join("\n")
                                }
                                isCopyable
                                size="sm"
                                alert="light"
                            />
                        </div>
                    </div>

                </Confirm>
            )}
            <Navbar
                className={`nav ${isLoggedIn ? "is-logged-in" : ""} ${navBackground} p-0 ${
                    shouldDisplayOnSide
                        ? `nav-side flex-lg-column position-absolute position-relative-lg rounded-bottom-end-sm`
                        : "ps-3 "
                } ${settings.isExpanded ? "expanded " : ""}`}
                expand="lg"
            >
                <div className={shouldDisplayOnSide
                    ? `d-none d-lg-flex align-items-center justify-content-between my-2 mx-0 w-100`
                    : ""}
                >
                    <Navbar.Brand
                        className={shouldDisplayOnSide ? navbarClasses.join(" ") : "m-0 p-0 me-2 my-2 d-block"}
                        onClick={shouldDisplayOnSide
                            ? () => setSettings({ ...settings, isExpanded: !settings.isExpanded })
                            : undefined}
                    >
                        <img
                            src={theme.isV3 ? logoV3 : logo}
                            alt="Foqal logo"
                            className={`mx-auto ${shouldDisplayOnSide
                                ? "nav-logo"
                                : "nav-logo-legacy"}`}
                        />
                    </Navbar.Brand>
                    {shouldDisplayOnSide && (
                        <button
                            type="button"
                            className={`btn btn-none ${theme.isV3 ? "" : "d-none text-blue-light"} expander me-2 p-1 ${
                                settings.isExpanded ? "d-lg-block" : ""}`}
                            onClick={() => setSettings({ ...settings, isExpanded: !settings.isExpanded })}
                        >
                            {(theme.isV3
                                ? (
                                    <RiArrowLeftDoubleLine
                                        size="24px"
                                        className={`nav-collapse-icon
                                        ${!settings.isExpanded ? ("nav-button-collapse rounded-circle") : "nav-button"}`}
                                    />
                                )
                                : <TbLayoutSidebarLeftCollapse size="20px" />)}
                        </button>
                    )}
                </div>

                <Navbar.Toggle aria-controls="responsive-navbar-nav" />

                <Navbar.Collapse id="responsive-navbar-nav" className="w-100">
                    <Nav className={`nav-responsive p-0 d-flex ${shouldDisplayOnSide
                        ? "h-100 w-100 flex-lg-column justify-content-between align-items-start"
                        : `w-100 justify-content-between align-items-center`}`}
                    >
                        <div className={`d-flex align-items-top ${
                            shouldDisplayOnSide
                                ? "flex-column align-items-center w-100 align-items-lg-start"
                                : "align-items-center"
                        }`}
                        >
                            {permissions.has(Permission.Administrator) && shouldDisplayOnSide && theme.isPageV3(Paths.HOME)
                                && (
                                    <NavLink
                                        to={Paths.HOME}
                                        className="navbar-link mb-3 w-100 text-center text-lg-start"
                                    >
                                        {shouldDisplayOnSide && <RocketIcon className="me-2 me-lg-0" />}
                                        <span className="d-inline ms-2 nav-label">
                                            Getting Started
                                        </span>
                                    </NavLink>

                                )}

                            {shouldDisplayOnSide ? (
                                <>
                                    {ticketsLink}
                                    {answersLink}
                                </>
                            ) : (
                                <>
                                    {answersLink}
                                    {ticketsLink}
                                </>
                            )}

                            {permissions.has(Permission.UsersCreate) && (

                                <NavLink
                                    to={Paths.CONTACTS}
                                    className={`navbar-link ${shouldDisplayOnSide
                                        ? `mb-3 w-100 text-center text-lg-start`
                                        : "mx-lg-0 mx-2 "}`}
                                >
                                    {shouldDisplayOnSide
                                        && (theme.isV3
                                            ? <RiTeamLine className="me-0 d-inline" size={ICON_SIZE} />
                                            : <FaUsers className="me-0 d-inline" size={ICON_SIZE} />)}
                                    <span className={shouldDisplayOnSide
                                        ? "d-inline ms-2 nav-label"
                                        : ""}
                                    >
                                        Contacts
                                    </span>

                                </NavLink>
                            )}

                            {permissions.has(Permission.Reports) && (

                                <NavLink
                                    to={Paths.REPORTS}
                                    className={`navbar-link ${shouldDisplayOnSide
                                        ? `mb-3 w-100 text-center text-lg-start`
                                        : "mx-lg-0 mx-2 "}`}
                                >
                                    {shouldDisplayOnSide
                                        && (theme.isV3
                                            ? <RiDashboardLine className="me-0 d-inline" size={ICON_SIZE} />
                                            : <FaChartPie className="me-0 d-inline" size={ICON_SIZE} />)}
                                    <span className={shouldDisplayOnSide
                                        ? "d-inline ms-2 nav-label"
                                        : ""}
                                    >
                                        Reports
                                    </span>

                                </NavLink>
                            )}

                            {!permissions.has(Permission.ChatsModify)
                                && (permissions.has(Permission.TicketsReadSelf) || permissions.has(Permission.TicketsRead))
                                && (
                                    <NavLink
                                        to={Paths.TICKETS}
                                        className="navbar-link position-relative"
                                    >
                                        {shouldDisplayOnSide && <IoTicketSharp className="me-2 me-lg-0" size={ICON_SIZE} />}
                                        <span className={shouldDisplayOnSide
                                            ? "d-inline ms-2 nav-label"
                                            : "d-block d-lg-none"}
                                        >
                                            My Tickets
                                        </span>
                                        {hasMessage && (
                                            <span
                                                className={"position-absolute top-0 start-100 translate-middle "
                                                    + "bg-danger badge p-1 rounded-circle"}
                                            >
                                                <MdOutlineMarkChatUnread />
                                                <span className="visually-hidden">New tickets</span>
                                            </span>
                                        )}
                                    </NavLink>
                                )}

                        </div>

                        <div className={`d-flex ${shouldDisplayOnSide
                            ? `flex-column align-items-lg-start ${settings.isExpanded
                                ? "align-items-start w-100"
                                : "align-items-center w-100"}`
                            : ` flex-column align-items-center flex-lg-row`}`}
                        >

                            {navigation?.links?.filterMap(link => (
                                <NavDropdown.Item
                                    href={link.url as string}
                                    target="_blank"
                                    className={`navbar-link  d-flex align-items-center ${shouldDisplayOnSide
                                        ? `mb-3 w-100 text-center text-lg-start ${!theme.isV3 ? "px-3" : ""}`
                                        : `mx-lg-0 mx-2 nav-icon-link ${!theme.isV3 ? "" : "my-0"}`}`}
                                    title={link.text as string}
                                >

                                    <Icon
                                        icon={PORTAL_SETTINGS_ICON_IMAGES[link.icon as PortalSettingsIcon]}
                                        className="me-0 d-inline"
                                        size={shouldDisplayOnSide ? "small" : "xsmall"}
                                    />

                                    <span className={shouldDisplayOnSide
                                        ? "d-inline ms-2 nav-label flex-grow-1"
                                        : "d-none "}
                                    >
                                        {link.text}
                                    </span>

                                </NavDropdown.Item>
                            ), link => (permissions.has(Permission.Administrator)
                                ? link.visibility !== PortalSettingsLinkVisibility.Public
                                : link.visibility !== PortalSettingsLinkVisibility.Internal))}

                            {navigation?.links?.isNotEmpty && (
                                (shouldDisplayOnSide ? (
                                    <div className="nav-icon-side-seperator" />
                                ) : (
                                    <div className="nav-icon-seperator" />
                                ))
                            )}

                            {!isLoggedIn && theme.isV3 && (
                                <Dropdown drop="down" align="end">
                                    <Dropdown.Toggle size="xsmall" variant="none">
                                        {theme.getTheme() === Themes.DARK ? (
                                            <Icon icon={<RiMoonLine />} variant="default" size="xsmall" />
                                        ) : (
                                            <Icon icon={<RiSunLine />} variant="default" size="xsmall" />
                                        )}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Item
                                            onClick={() => {
                                                theme.resetTheme();
                                            }}
                                            prefixIcon={<RiComputerLine />}
                                        >
                                            System
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            onClick={() => {
                                                theme.setTheme(Themes.LIGHT);
                                            }}
                                            prefixIcon={<RiSunLine />}
                                        >
                                            Light Mode
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            onClick={() => {
                                                theme.setTheme(Themes.DARK);
                                            }}
                                            prefixIcon={<RiMoonLine />}
                                        >
                                            Dark Mode
                                        </Dropdown.Item>

                                    </Dropdown.Menu>
                                </Dropdown>

                            )}

                            {!theme.isV3 && featureFlags.has(FEATURE_SHOW_PAGE_HELP) && helpContext.getArticleId() && (
                                <Help
                                    articleId={helpContext.getArticleId()}
                                    buttonClassName={`navbar-link btn btn-none position-relative ${
                                        shouldDisplayOnSide ? `${theme.isV3 ? "" : "py-0"} mb-2` : "p-0 mx-3"}`}
                                    button={(
                                        <>
                                            {(theme.isV3
                                                ? <RiQuestionLine size={ICON_SIZE} />
                                                : <FiHelpCircle className="fs-5" />)}
                                            <span className={
                                                shouldDisplayOnSide ? "ms-2 nav-label" : "d-lg-none ms-2 "
                                            }
                                            >
                                                Help
                                            </span>
                                        </>
                                    )}
                                />
                            )}

                            {(permissions.has(Permission.ChatsModifyAll)) && (
                                <button
                                    type="button"
                                    onClick={() => updates.show()}
                                    className={`navbar-link btn btn-none position-relative ${
                                        shouldDisplayOnSide ? `${theme.isV3 ? "" : "py-0"}` : "p-0 mx-3 my-2"}`}
                                >
                                    {(theme.isV3
                                        ? <RiGiftLine size={ICON_SIZE} />
                                        : <AiOutlineGift className="fs-5" />)}
                                    <span className={
                                        shouldDisplayOnSide ? "ms-2 nav-label" : "d-lg-none ms-2 "
                                    }
                                    >
                                        Updates
                                    </span>
                                    {updates.unreadCount() > 0 && (
                                        <span
                                            className={classNames(
                                                theme.isV3
                                                    ? "nav-updates-icon"
                                                    : (" position-absolute top-0 start-75 translate-middle "
                                                    + "bg-button-hover badge rounded-circle p-1 "),
                                            )}
                                        >
                                            {theme.isV3
                                                ? <RiLightbulbFlashFill size={12} />
                                                : <MdOutlineTipsAndUpdates />}
                                            <span className="visually-hidden">New Updates</span>
                                        </span>
                                    )}
                                </button>
                            )}
                            {!theme.isV3
                            && (permissions.has(Permission.ArticleModify)
                                || permissions.has(Permission.TicketsModify)
                                || permissions.has(Permission.TicketCreatePublic)
                                || permissions.has(Permission.TicketCreateGuest)
                            )
                                && (
                                    <NavDropdown
                                        data-test="create-dropdown"
                                        drop={shouldDisplayOnSide ? "up" : undefined}
                                        align={shouldDisplayOnSide ? "start" : "end"}
                                        title={(
                                            <span>
                                                <BsPlusSquare className="fs-5" />
                                                <span className={
                                                    shouldDisplayOnSide ? "ms-2 nav-label" : "d-lg-none ms-2"
                                                }
                                                >
                                                    Create
                                                </span>
                                            </span>
                                        )}
                                        bsPrefix="navbar-link"
                                        className={`navdropdown-link m-lg-0 m-2 menu-small collapsible-nav-dropdown ${
                                            !shouldDisplayOnSide ? "p-0 align-self-center" : ""
                                        }`}
                                    >
                                        {permissions.has(Permission.ArticleModify) && (
                                            <NavDropdown.Item
                                                eventKey="3"
                                                data-test="new-answer"
                                                onClick={createHistoryPush(Paths.ANSWERS_NEW)}
                                            >
                                                Article
                                            </NavDropdown.Item>
                                        )}
                                        {permissions.has(Permission.TicketsModify) && (
                                            <NavDropdown.Item
                                                eventKey="4"
                                                data-test="agent-compose"
                                                onClick={createHistoryPush(Paths.AGENT_COMPOSE)}
                                            >
                                                Compose Message
                                            </NavDropdown.Item>
                                        )}
                                        {(
                                            permissions.has(Permission.TicketCreatePublic)
                                            || permissions.has(Permission.TicketCreateGuest)
                                            || permissions.has(Permission.ChatsModify)
                                        )
                                            && (
                                                <NavDropdown.Item
                                                    eventKey="5"
                                                    data-test="ticket-create"
                                                    onClick={() => createTicket.show()}
                                                >
                                                    Request
                                                </NavDropdown.Item>
                                            )}

                                    </NavDropdown>

                                )}
                            {(permissions.has(Permission.Administrator)) && (
                                <NavLink
                                    to={Paths.ADMINISTRATOR}
                                    className={`navbar-link
                                     ${shouldDisplayOnSide ? "" : "m-lg-0 mx-5 align-self-center"}
                                     ${theme.isV3 ? "w-100" : ""}`}
                                >
                                    {(theme.isV3
                                        ? <RiSettings3Line size={ICON_SIZE} />
                                        : <BsGearFill className="fs-5" />)}
                                    <span className={
                                        shouldDisplayOnSide ? "ms-2 nav-label" : "d-lg-none ms-2 "
                                    }
                                    >
                                        Settings
                                    </span>
                                </NavLink>
                            )}
                            {(!isLoggedIn || !user) && (
                                <NavLink
                                    to={Paths.LOGIN}
                                    className="navbar-link text-center"
                                >
                                    Sign In
                                </NavLink>
                            )}

                            {(isLoggedIn && user) && (
                                <NavDropdown
                                    data-test="user-account"
                                    drop={shouldDisplayOnSide ? "up" : undefined}
                                    align={shouldDisplayOnSide ? "start" : "end"}
                                    title={(
                                        <>
                                            <Avatar
                                                src={(theme.isV3
                                                    ? user?.avatarUrl as string
                                                    : "")}
                                                variant="sm"
                                                className={settings.isExpanded ? "me-2" : ""}
                                                avatarStatusColor={assignmentPreference === AgentAssignmentPreference.Unavailable
                                                    ? "empty"
                                                    : "success"}
                                                defaultIcon={
                                                    (
                                                        !theme.isV3 ? (
                                                            <IoPerson className={!shouldDisplayOnSide
                                                                ? `${settings.isExpanded ? "me-2" : ""}`
                                                                : ""}
                                                            />
                                                        ) : (
                                                            <div className="nav-user-icon">
                                                                <RiUser3Fill />
                                                            </div>
                                                        )
                                                    // eslint-disable-next-line react/jsx-indent
                                                    )
                                                }
                                            />

                                            <span className={
                                                shouldDisplayOnSide ? `ms-2 nav-label` : "d-lg-none ms-2 "
                                            }
                                            >
                                                Profile
                                            </span>

                                        </>
                                    )}
                                    bsPrefix="navbar-link"
                                    className={`navdropdown-link menu-small ${theme.isV3 ? "p-0 collapsible-nav-dropdown" : ""}`}
                                >
                                    <NavDropdown.Header className="d-flex flex-column align-items-end mb-2">
                                        <div className="d-flex w-100 mb-2">
                                            <Avatar
                                                src={user.avatarUrl as string}
                                                isCircle
                                                variant="sm-3"
                                            />
                                            <div>
                                                <div className="fs-6">
                                                    {user.name}
                                                </div>

                                                <button
                                                    type="button"
                                                    onClick={() => setShowDebug(true)}
                                                    className="btn btn-none text-light fs-6 p-0"
                                                >
                                                    <small>
                                                        Support Info
                                                    </small>
                                                </button>
                                            </div>
                                        </div>
                                    </NavDropdown.Header>
                                    {theme.isV3 && (

                                        <Dropdown className="w-100" drop="end">
                                            <Dropdown.Toggle
                                                variant="none"
                                                className="w-100 d-flex py-0 ps-2 pe-1 dropdown-item"
                                            >

                                                <Icon
                                                    size="xsmall"
                                                    variant="default"
                                                    className="me-2"
                                                    icon={<RiEditCircleLine />}
                                                />

                                                <div>
                                                    Your Status
                                                </div>

                                                <div className="nav-theme-picker flex-grow-1 ms-3 text-end">
                                                    {assignmentStatus.label}
                                                </div>

                                                <Icon
                                                    icon={<RiArrowRightSLine />}
                                                    size="xsmall"
                                                    className="nav-theme-picker icon-size-fit"
                                                />

                                            </Dropdown.Toggle>
                                            <Dropdown.Menu className="nav-side-child-menu">
                                                {ASSIGNMENT_PREFERENCES.map(({ label, value, icon }) => (
                                                    <Dropdown.Item
                                                        key={value}
                                                        onClick={() => saveAssignmentPreference?.(value)}
                                                        className="py-0"
                                                    >

                                                        <Icon size="xsmall" className="me-2" icon={icon} />

                                                        <div className="flex-grow-1">
                                                            {label}
                                                        </div>

                                                    </Dropdown.Item>
                                                ))}

                                            </Dropdown.Menu>
                                        </Dropdown>
                                    )}

                                    <Dropdown className="w-100" drop="end">
                                        <Dropdown.Toggle
                                            variant={theme.isV3 ? "none" : "outline-gray"}
                                            size={theme.isV3 ? undefined : "xsmall"}
                                            className={`dropdown-item py-0 ${theme.isV3 ? "ps-2 pe-1" : "w-100 d-flex px-3"}`}
                                        >
                                            {theme.isV3 && (
                                                <Icon
                                                    size="xsmall"
                                                    variant="default"
                                                    className="me-2"
                                                    icon={<RiPaintBrushLine />}
                                                />
                                            )}

                                            <div>
                                                Interface Theme
                                            </div>

                                            <div className="nav-theme-picker flex-grow-1 ms-3 text-end">
                                                {theme.getTheme()}
                                            </div>
                                            {theme.isV3 && (
                                                <Icon
                                                    icon={<RiArrowRightSLine />}
                                                    size="small"
                                                    className="nav-theme-picker icon-size-fit"
                                                />
                                            )}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu className="nav-side-child-menu">

                                            <Dropdown.Item
                                                onClick={() => theme.resetTheme()}
                                            >
                                                {theme.isV3 && (
                                                    <Icon
                                                        size="xsmall"
                                                        className="me-2"
                                                        variant="default"
                                                        icon={<RiComputerLine />}
                                                    />
                                                )}
                                                System
                                            </Dropdown.Item>

                                            <Dropdown.Item
                                                onClick={() => theme.setTheme(Themes.DEFAULT)}
                                            >
                                                {theme.isV3 && (
                                                    <Icon
                                                        size="xsmall"
                                                        className="me-2"
                                                        variant="default"
                                                        icon={<RiAccountCircleLine />}
                                                    />
                                                )}
                                                Legacy
                                            </Dropdown.Item>
                                            <Dropdown.Item
                                                onClick={() => theme.setTheme(Themes.LIGHT)}
                                            >
                                                {theme.isV3 && (
                                                    <Icon
                                                        size="xsmall"
                                                        className="me-2"
                                                        variant="default"
                                                        icon={<RiSunLine />}
                                                    />
                                                )}
                                                Light
                                            </Dropdown.Item>
                                            <Dropdown.Item
                                                onClick={() => theme.setTheme(Themes.DARK)}
                                            >
                                                {theme.isV3 && (
                                                    <Icon
                                                        size="xsmall"
                                                        className="me-2"
                                                        variant="default"
                                                        icon={<RiMoonLine />}
                                                    />
                                                )}
                                                Dark
                                            </Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>

                                    {/* <NavDropdown.Divider className="mx-3 mb-4 mt-0" /> */}
                                    <NavDropdown.Divider className="my-2" />

                                    {permissions.has(Permission.SuperAdmin) && (
                                        <NavLink
                                            to={Paths.SITE_ADMINISTRATOR}
                                            className="text-start dropdown-item justify-content-start"
                                        >
                                            {theme.isV3 && (
                                                <Icon
                                                    size="xsmall"
                                                    variant="default"
                                                    className="me-2"
                                                    icon={<RiAdminLine />}
                                                />

                                            )}
                                            Site Admin
                                        </NavLink>
                                    )}

                                    {permissions.has(Permission.ChatsModifyAll) && (
                                        <NavLink
                                            to={Paths.ACCOUNT_PROFLE}
                                            className="text-start dropdown-item justify-content-start"
                                        >
                                            {theme.isV3 && (
                                                <Icon
                                                    size="xsmall"
                                                    variant="default"
                                                    className="me-2"
                                                    icon={<RiAccountCircleLine />}
                                                />
                                            )}
                                            My Profile
                                        </NavLink>
                                    )}

                                    {permissions.has(Permission.ChatsModify) && (
                                        <NavLink
                                            to={Paths.TICKETS}
                                            className="text-start dropdown-item justify-content-start"
                                        >
                                            {theme.isV3 && (
                                                <Icon
                                                    size="xsmall"
                                                    variant="default"
                                                    className="me-2"
                                                    icon={<RiPriceTag3Line />}
                                                />

                                            )}
                                            My Tickets
                                        </NavLink>
                                    )}

                                    <NavDropdown.Divider className="my-2" />

                                    <NavDropdown.Item
                                        onClick={logout}
                                        className="text-start dropdown-item justify-content-start"
                                        data-test="logout"
                                    >

                                        {theme.isV3 && (
                                            <Icon
                                                size="xsmall"
                                                variant="default"
                                                className="me-2"
                                                icon={<RiLogoutBoxLine />}
                                            />
                                        )}

                                        Logout
                                    </NavDropdown.Item>
                                </NavDropdown>
                            )}
                        </div>
                    </Nav>
                </Navbar.Collapse>
            </Navbar>
        </>
    );
}
Navigation.defaultProps = {
    shouldDisplayOnSide: false,
};
